import classNames from 'classnames';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Button } from '../Button/Button';
import { formatAddress, formatMoney, labelFromValue } from '../../util';
import { H3 } from '../H3/H3';
import { PackageTypes, ContentTypes } from '../../types';
import { Text } from '../Text/Text';
import { useStore } from '../../store';
import styles from './OrderSummary.module.scss';
import { BRAND_COLORS } from '../../config';

type Props = {
  className?: string;
  packageDetailsEditEnabled: boolean;
  quoteEditEnabled: boolean;
  addressDetailsEditEnabled: boolean;
  variant?: 'default' | 'payment-package-card';
};

export function OrderSummary(props: Props) {
  const {
    className,
    packageDetailsEditEnabled,
    quoteEditEnabled,
    addressDetailsEditEnabled,
    variant = 'default',
  } = props;

  const items = useStore((s) => s.items);
  const quotePrice = useStore((s) => s.quotePrice);
  const reQuoteMessage = useStore((s) => s.reQuoteMessage);
  const quoteDelivery = useStore((s) => s.quoteDelivery);
  const pickupAddress1 = useStore((s) => s.pickupAddress1);
  const pickupAddress2 = useStore((s) => s.pickupAddress2);
  const pickupSuburb = useStore((s) => s.pickupSuburb);
  const pickupState = useStore((s) => s.pickupState);
  const pickupPostcode = useStore((s) => s.pickupPostcode);
  const destinationAddress1 = useStore((s) => s.destinationAddress1);
  const destinationAddress2 = useStore((s) => s.destinationAddress2);
  const destinationSuburb = useStore((s) => s.destinationSuburb);
  const destinationState = useStore((s) => s.destinationState);
  const destinationPostcode = useStore((s) => s.destinationPostcode);
  // const extendedLiability = useStore((s) => s.extendedLiability);
  const rawInsuranceFee = useStore((state) => state.insuranceFee);
  const hideInsuranceFields = useStore((state) => state.hideInsuranceFields);
  const noPrinter = useStore((state) => state.noPrinter);
  const couponDiscountPercentage = useStore((state) => state.couponDiscountPercentage);
  const courierServiceName = useStore((s) => s.courierServiceName);

  // Reset insurance fee when content type doesn't support insurance
  const insuranceFee = hideInsuranceFields ? '$0' : rawInsuranceFee;
  const hasInsuranceFee = !hideInsuranceFields && !!insuranceFee && insuranceFee !== '$0' && Number(insuranceFee.replace('$', '')) > 0;

  const restrictedContentsList = ['plants', 'artwork', 'household contents/personal effects'];
  const restrictedItems = (items || []).filter(
    (item: any) => item.contents && restrictedContentsList.includes(item.contents.toLowerCase())
  );
  const restrictedLabels = Array.from(
    new Set(
      restrictedItems.map(
        (item: any) => item.contents.charAt(0).toUpperCase() + item.contents.slice(1)
      )
    )
  );

  const restrictedTooltipText = restrictedLabels.length > 0
    ? `This consignment contains restricted ${restrictedLabels.length > 1 ? 'items' : 'item'} (${restrictedLabels.join(', ')}), so insurance is not covered.`
    : '';

  const isInsuranceNotApplicable = hideInsuranceFields || restrictedLabels.length > 0;

  //calculate order net amount
  const calculateNetAmt = () => {
    let quoteAmt = quotePrice || 0;
    let insuranceAmt = insuranceFee ? Number(insuranceFee.replace('$', '')) : 0;
    let manualConnoteAmt = noPrinter ? 30 : 0;
    let subAmt = quoteAmt + insuranceAmt + manualConnoteAmt;
    if(couponDiscountPercentage){
      subAmt = (subAmt) - (subAmt * Number(couponDiscountPercentage)/100);
    };
    return (subAmt + subAmt * 0.1).toFixed(2);
  }

  const selectedCourierName =
    courierServiceName === 'Toll'
      ? 'Fast Courier'
      : courierServiceName === 'Toll Palletised Express'
      ? 'Fast Courier Palletised Express'
      : courierServiceName;

  const discountAmount = couponDiscountPercentage
    ? (
        (Number(couponDiscountPercentage) / 100) *
        ((quotePrice || 0) + (noPrinter ? 30 : 0) + Number(insuranceFee.replace('$', '')))
      ).toFixed(2)
    : null;

  return (
    <div className={classNames(styles.root, { [styles.paymentPackageCard]: variant === 'payment-package-card' }, className)}>
      <div className={classNames(styles.columns, { [styles.columnsPayment]: variant === 'payment-package-card' })}>
        <div
          className={
            variant === 'payment-package-card'
              ? classNames(styles.columnPayment, styles.summaryCard)
              : styles.column
          }
        >
          <H3 className={classNames(styles.heading, { [styles.headingPayment]: variant === 'payment-package-card' })}>
            {variant === 'payment-package-card' && (
              <span className={styles.sectionHeadingIcon}>
                <FontAwesomeIcon icon={faBox} />
              </span>
            )}
            Package Details
            {packageDetailsEditEnabled && (
              <Button
                type="link"
                size="small"
                to="/package-details/"
                className={classNames(styles.link, { [styles.linkPayment]: variant === 'payment-package-card' })}
              >
                {variant === 'payment-package-card' ? 'Edit ›' : '(edit)'}
              </Button>
            )}
          </H3>
          {variant === 'payment-package-card' ? (
            <>
              {items?.map((item, i) => (
                <div className={styles.summarySection} key={i}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Type</span>
                    <span className={styles.summaryValue}>{labelFromValue(item.type, PackageTypes)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Weight</span>
                    <span className={styles.summaryValue}>{item.weight} kg</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Dimensions</span>
                    <span className={styles.summaryValue}>(L) {item.length}cm x (W) {item.width}cm x (H) {item.height}cm</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Quantity</span>
                    <span className={styles.summaryValue}>{item.quantity}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Contents</span>
                    <span className={styles.summaryValue}>{labelFromValue(item.contents, ContentTypes)}</span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {items?.map((item, i) => (
                <ul className={styles.list} key={i}>
                  <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                    <strong>Package Type:</strong> {labelFromValue(item.type, PackageTypes)}
                  </Text>
                  <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                    <strong>Weight:</strong> {item.weight}kg
                  </Text>
                  <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                    <strong>Dimensions:</strong> (L) {item.length}cm x (W) {item.width}cm x (H) {item.height}
                    cm
                  </Text>
                  <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                    <strong>Quantity:</strong> {item.quantity}
                  </Text>
                  <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                    <strong>Contents:</strong> {labelFromValue(item.contents, ContentTypes)}
                  </Text>
                </ul>
              ))}
              <ul className={styles.listCourierName}>
                <Text size="small" as="li" className={classNames(styles.item, styles.itemSmall)}>
                  <strong>Courier Selected:</strong> {selectedCourierName}
                </Text>
              </ul>
            </>
          )}
        </div>
        {variant !== 'payment-package-card' ? (
          <>
            <div className={styles.column}>
              <H3 className={styles.heading}>
                Quote
                {quoteEditEnabled && (
                  <Button type="link" size="small" to="/quotes-comparisons/" className={styles.link}>
                    (edit)
                  </Button>
                )}
              </H3>
              <ul className={styles.list}>
            {/* {noteTitle && (
              <Text size="small" as="li" className={styles.item}>
                <strong>{noteTitle}</strong>
                {noteSubTitle && <p>{noteSubTitle}</p>}
                {noteBody && (
                  <div className={styles.insurance}>
                    <InfoCircle className={styles.insuranceIcon} />
                    <span dangerouslySetInnerHTML={{ __html: noteBody }} />
                  </div>
                )}
              </Text>
            )} */}
            <Text size="small" as="li" className={styles.item}>
              <strong>Est. Delivery: </strong>
              {quoteDelivery}
            </Text>
            <Text size="small" as="li" className={styles.item}>
              <strong>Price: </strong>
              {formatMoney(quotePrice)} (Excl. GST)
            </Text>

            <Text size="small" as="li" className={styles.item}>
              <strong>Warranty: </strong>
              {isInsuranceNotApplicable ? 'Not Covered' : hasInsuranceFee ? insuranceFee : '$0'}
              {restrictedLabels.length > 0 && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="summary-warranty-tooltip">
                      {restrictedTooltipText}
                    </Tooltip>
                  }
                >
                  <span style={{ marginLeft: 6, cursor: 'pointer', color: '#f59e0b', display: 'inline-flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </span>
                </OverlayTrigger>
              )}
            </Text>

            {noPrinter ? (
              <Text size="small" as="li" className={styles.item}>
                <strong>Mannual Connotes: </strong>
                $30.00
              </Text>
            ) : (
              ''
            )}

            {couponDiscountPercentage ? (
              <Text size="small" as="li" className={styles.item}>
                <strong>Discount: </strong>
                {'$'+((Number(couponDiscountPercentage)/100) * ((quotePrice || 0) + (noPrinter ? 30 : 0) + Number(insuranceFee.replace('$', '')))).toFixed(2) + ' (' + couponDiscountPercentage + '%)'}
              </Text>
            ) : (
              ''
            )}

            <Text size="small" as="li" className={styles.item}>
              <strong>Net Price: </strong>${calculateNetAmt()} (Inc. GST)
            </Text>
            {reQuoteMessage && (
              <Text size="small" as="li" className={styles.item}>
                {reQuoteMessage === 'estimated' ? (
                  <span style={{ color: BRAND_COLORS.primary, fontSize: '12px' }}>
                    Estimated price{' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="estimated-price-tooltip">
                          This is an estimated price. The final price may differ based on the actual street address.
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#6c757d' }} />
                      </span>
                    </OverlayTrigger>
                  </span>
                ) : (
                  <span style={{ color: '#0d6efd', fontSize: '12px' }}>
                    Price updated{' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="requote-price-default-tooltip">
                          {reQuoteMessage}
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </OverlayTrigger>
                  </span>
                )}
              </Text>
            )}
              </ul>
            </div>
            <div className={styles.column}>
              <H3 className={styles.heading}>
                Address Details
                {addressDetailsEditEnabled && (
                  <Button type="link" size="small" to="/shipment-details/" className={styles.link}>
                    (edit)
                  </Button>
                )}
              </H3>
              <ul className={styles.list}>
                <Text size="small" as="li" className={styles.item}>
                  <strong>From: </strong>
                  <div className={styles.address}>
                    {formatAddress(pickupAddress1, pickupAddress2, pickupSuburb, pickupState, pickupPostcode)}
                  </div>
                </Text>
                <Text size="small" as="li" className={styles.item}>
                  <strong>To: </strong>
                  <div className={styles.address}>
                    {formatAddress(
                      destinationAddress1,
                      destinationAddress2,
                      destinationSuburb,
                      destinationState,
                      destinationPostcode
                    )}
                  </div>
                </Text>
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className={classNames(styles.columnPayment, styles.summaryCard)}>
              <H3 className={classNames(styles.heading, styles.headingPayment)}>
                <span className={styles.sectionHeadingIcon}>
                  <FontAwesomeIcon icon={faReceipt} />
                </span>
                Quote
                {quoteEditEnabled && (
                  <Button type="link" size="small" to="/quotes-comparisons/" className={classNames(styles.link, styles.linkPayment)}>
                    Edit ›
                  </Button>
                )}
              </H3>
              <div className={styles.summarySection}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Courier</span>
                  <span className={styles.summaryValue}>{selectedCourierName}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Est. Delivery</span>
                  <span className={styles.summaryValue}>{quoteDelivery}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Price (Excl. GST)</span>
                  <span className={styles.summaryValue}>{formatMoney(quotePrice)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Warranty</span>
                  <span className={styles.summaryValue}>
                    {isInsuranceNotApplicable ? 'Not Covered' : hasInsuranceFee ? insuranceFee : '$0'}
                    {restrictedLabels.length > 0 && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="summary-payment-warranty-tooltip">
                            {restrictedTooltipText}
                          </Tooltip>
                        }
                      >
                        <span style={{ marginLeft: 6, cursor: 'pointer', color: '#f59e0b', display: 'inline-flex', alignItems: 'center' }}>
                          <FontAwesomeIcon icon={faCircleInfo} />
                        </span>
                      </OverlayTrigger>
                    )}
                  </span>
                </div>
                {noPrinter ? (
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Manual Connotes</span>
                    <span className={styles.summaryValue}>$30.00</span>
                  </div>
                ) : null}
                {couponDiscountPercentage && discountAmount ? (
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Discount ({couponDiscountPercentage}%)</span>
                    <span className={styles.summaryValue}>-${discountAmount}</span>
                  </div>
                ) : null}

                <div className={classNames(styles.summaryRow, styles.totalRow)}>
                  <span className={styles.totalLabel}>Total (Inc. GST)</span>
                  <span className={styles.totalValue}>${calculateNetAmt()}</span>
                </div>
                {reQuoteMessage && (
                  <div style={{ marginTop: '8px', textAlign: 'right' }}>
                    {reQuoteMessage === 'estimated' ? (
                      <span style={{ color: BRAND_COLORS.primary, fontSize: '12px' }}>
                        Estimated price{' '}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="estimated-price-summary-tooltip">
                              This is an estimated price. The final price may differ based on the actual street address.
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#6c757d' }} />
                          </span>
                        </OverlayTrigger>
                      </span>
                    ) : (
                      <span style={{ color: '#0d6efd', fontSize: '12px' }}>
                        Price updated{' '}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="requote-price-summary-tooltip">
                              {reQuoteMessage}
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'pointer' }}>
                            <FontAwesomeIcon icon={faCircleInfo} />
                          </span>
                        </OverlayTrigger>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={classNames(styles.columnPayment, styles.summaryCard)}>
              <H3 className={classNames(styles.heading, styles.headingPayment)}>
                <span className={styles.sectionLabelIcon}>
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                Address Details
                {addressDetailsEditEnabled && (
                  <Button type="link" size="small" to="/shipment-details/" className={classNames(styles.link, styles.linkPayment)}>
                    Edit ›
                  </Button>
                )}
              </H3>
              <div className={styles.summarySection}>
                <div className={styles.addressBlock}>
                  <div className={styles.addressLabel}>From</div>
                  <div className={styles.addressPrimary}>{pickupSuburb}, {pickupState} {pickupPostcode}</div>
                  <div className={styles.addressSecondary}>{pickupAddress1}{pickupAddress2 ? `, ${pickupAddress2}` : ''}</div>
                </div>
                <div className={styles.addressBlock}>
                  <div className={styles.addressLabel}>To</div>
                  <div className={styles.addressPrimary}>{destinationSuburb}, {destinationState} {destinationPostcode}</div>
                  <div className={styles.addressSecondary}>
                    {destinationAddress1}{destinationAddress2 ? `, ${destinationAddress2}` : ''}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
